<template>
  <div
    class="medhop-assistant"
    :class="{ 'medhop-assistant--open': open, 'medhop-assistant--dragging': dragging }"
    :style="positionStyle"
  >
    <button
      class="medhop-assistant__trigger"
      type="button"
      aria-label="Buka Aime, AI Assistant Medhop"
      :aria-expanded="open ? 'true' : 'false'"
      @pointerdown="startDrag"
      @click="toggleAssistant"
    >
      <span class="medhop-assistant__trigger-avatar" aria-hidden="true">
        <i v-if="open" class="fas fa-times"></i>
        <img v-else src="/images/aime-avatar.png" alt="" draggable="false" />
        <small v-if="!open"><i class="fas fa-star"></i></small>
      </span>
      <span class="medhop-assistant__trigger-copy">
        <strong>Aime</strong>
        <small>Asisten Virtual</small>
      </span>
    </button>

    <transition name="assistant-panel">
      <section v-if="open" class="medhop-assistant__panel" aria-label="Aime, AI Assistant Medhop">
        <header class="medhop-assistant__header">
          <div class="medhop-assistant__avatar"><img src="/images/aime-avatar.png" alt="Avatar Aime" /></div>
          <div>
            <strong>Aime</strong>
            <span>AI Assistant Medhop · Beta</span>
          </div>
          <button type="button" aria-label="Tutup assistant" @click="open = false">
            <i class="fas fa-times"></i>
          </button>
        </header>

        <div ref="messages" class="medhop-assistant__messages">
          <article v-for="(message, index) in messages" :key="index" :class="`is-${message.role}`">
            <span>{{ message.text }}</span>
          </article>
        </div>

        <div class="medhop-assistant__context">
          <small>Saran di halaman ini</small>
          <div>
            <button
              v-for="action in contextualActions"
              :key="action.label"
              type="button"
              @click="runAction(action)"
            >
              <i :class="action.icon"></i>{{ action.label }}
            </button>
          </div>
        </div>

        <form class="medhop-assistant__composer" @submit.prevent="sendMessage">
          <input
            v-model.trim="question"
            type="text"
            maxlength="240"
            autocomplete="off"
            placeholder="Tanya alur kerja atau cari menu…"
            aria-label="Pertanyaan untuk Medhop Assistant"
          />
          <button type="submit" :disabled="!question" aria-label="Kirim pertanyaan">
            <i class="fas fa-arrow-up"></i>
          </button>
        </form>

        <p class="medhop-assistant__notice">
          Tidak digunakan untuk diagnosis, resep, atau keputusan klinis.
        </p>
      </section>
    </transition>
  </div>
</template>

<script>
export default {
  name: "MedhopAssistant",
  data() {
    return {
      open: false,
      question: "",
      messages: [
        {
          role: "assistant",
          text: "Halo, saya Aime. Saya bantu mencari menu dan menjelaskan alur operasional Medhop.",
        },
      ],
      position: null,
      dragging: false,
      movedWhileDragging: false,
      dragOrigin: null,
    };
  },
  mounted() {
    this.restorePosition();
    window.addEventListener("resize", this.keepInViewport);
  },
  beforeDestroy() {
    this.stopDrag();
    window.removeEventListener("resize", this.keepInViewport);
  },
  computed: {
    positionStyle() {
      if (!this.position) return null;
      return { left: `${this.position.x}px`, top: `${this.position.y}px`, right: "auto", bottom: "auto" };
    },
    contextualActions() {
      const path = this.$route.path;
      if (path.startsWith("/appointment")) {
        return [
          this.action("Buat perjanjian", "/appointment/add", "fas fa-calendar-plus"),
          this.action("Lihat pasien", "/patient/list", "fas fa-user-injured"),
        ];
      }
      if (path.startsWith("/patient")) {
        return [
          this.action("Tambah pasien", "/patient/add", "fas fa-user-plus"),
          this.action("Buat perjanjian", "/appointment/add", "fas fa-calendar-plus"),
        ];
      }
      if (path.startsWith("/payment")) {
        return [
          this.action("Daftar pembayaran", "/payment/list", "fas fa-cash-register"),
          this.action("Cari pasien", "/patient/list", "fas fa-search"),
        ];
      }
      if (path.startsWith("/medicine") || path.startsWith("/pharmacy")) {
        return [
          this.action("Daftar obat", "/medicine/list", "fas fa-pills"),
          this.action("Stok obat", "/medicine/stock", "fas fa-boxes"),
        ];
      }
      return [
        this.action("Daftar pasien", "/patient/list", "fas fa-user-injured"),
        this.action("Buat perjanjian", "/appointment/add", "fas fa-calendar-plus"),
        this.action("Pembayaran", "/payment/list", "fas fa-cash-register"),
      ];
    },
  },
  watch: {
    "$route.path"() {
      if (window.innerWidth < 768) this.open = false;
    },
  },
  methods: {
    toggleAssistant() {
      if (this.movedWhileDragging) {
        this.movedWhileDragging = false;
        return;
      }
      this.open = !this.open;
    },
    startDrag(event) {
      if (event.button !== undefined && event.button !== 0) return;
      const rect = this.$el.getBoundingClientRect();
      this.dragging = true;
      this.movedWhileDragging = false;
      this.dragOrigin = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        elementX: rect.left,
        elementY: rect.top,
      };
      window.addEventListener("pointermove", this.onDrag);
      window.addEventListener("pointerup", this.stopDrag, { once: true });
    },
    onDrag(event) {
      if (!this.dragging || !this.dragOrigin) return;
      const dx = event.clientX - this.dragOrigin.pointerX;
      const dy = event.clientY - this.dragOrigin.pointerY;
      if (Math.abs(dx) + Math.abs(dy) > 5) this.movedWhileDragging = true;
      const rect = this.$el.getBoundingClientRect();
      const padding = 10;
      this.position = {
        x: Math.min(Math.max(padding, this.dragOrigin.elementX + dx), window.innerWidth - rect.width - padding),
        y: Math.min(Math.max(padding, this.dragOrigin.elementY + dy), window.innerHeight - rect.height - padding),
      };
    },
    stopDrag() {
      if (!this.dragging) return;
      this.dragging = false;
      this.dragOrigin = null;
      window.removeEventListener("pointermove", this.onDrag);
      if (this.position) localStorage.setItem("medhop_aime_position", JSON.stringify(this.position));
    },
    restorePosition() {
      try {
        const saved = JSON.parse(localStorage.getItem("medhop_aime_position"));
        if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) this.position = saved;
      } catch (_) {
        localStorage.removeItem("medhop_aime_position");
      }
      this.$nextTick(this.keepInViewport);
    },
    keepInViewport() {
      if (!this.position || !this.$el) return;
      const rect = this.$el.getBoundingClientRect();
      this.position = {
        x: Math.min(Math.max(10, this.position.x), Math.max(10, window.innerWidth - rect.width - 10)),
        y: Math.min(Math.max(10, this.position.y), Math.max(10, window.innerHeight - rect.height - 10)),
      };
    },
    action(label, route, icon) {
      return { label, route, icon };
    },
    runAction(action) {
      if (this.$route.path !== action.route) this.$router.push(action.route).catch(() => {});
      if (window.innerWidth < 768) this.open = false;
    },
    sendMessage() {
      if (!this.question) return;
      const question = this.question;
      this.messages.push({ role: "user", text: question });
      this.question = "";
      this.messages.push({ role: "assistant", text: this.answer(question) });
      this.$nextTick(() => {
        if (this.$refs.messages) this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight;
      });
    },
    answer(input) {
      const text = input.toLowerCase();
      if (/pasien|pendaftaran/.test(text)) {
        return "Untuk pasien baru: buka Pasien → Tambah Pasien, lengkapi identitas, lalu buat perjanjian dari menu Pelayanan Pasien.";
      }
      if (/janji|jadwal|appointment/.test(text)) {
        return "Buka Pelayanan Pasien → Pendaftaran atau Perjanjian. Gunakan Hari Ini untuk antrean berjalan dan Kalender untuk melihat kapasitas jadwal.";
      }
      if (/bayar|kasir|invoice/.test(text)) {
        return "Buka Kasir & Pendapatan → Pembayaran. Cari invoice pasien, periksa rincian layanan, lalu catat metode pembayaran.";
      }
      if (/obat|farmasi|stok|apotek/.test(text)) {
        return "Buka Farmasi & Stok. Periksa batch dan stok sebelum transaksi; obat mendekati kedaluwarsa tersedia pada notifikasi bagian atas.";
      }
      if (/dokter|praktik/.test(text)) {
        return "Gunakan Jadwal Praktik Dokter untuk jam layanan dan Perjanjian Hari Ini untuk daftar pasien yang perlu dilayani.";
      }
      if (/diagnos|resep|dosis|penyakit/.test(text)) {
        return "Saya tidak memberi diagnosis, dosis, atau keputusan klinis. Gunakan modul rekam medis dan verifikasi keputusan dengan tenaga medis berwenang.";
      }
      return "Saya bisa membantu alur pasien, perjanjian, pembayaran, jadwal dokter, farmasi, stok, dan menemukan menu operasional.";
    },
  },
};
</script>

<style scoped>
.medhop-assistant { position: fixed; right: 22px; bottom: 22px; z-index: 1090; user-select: none; touch-action: none; }
.medhop-assistant__trigger { display: inline-flex; align-items: center; gap: 10px; min-height: 58px; padding: 5px 15px 5px 6px; color: #fff !important; background: linear-gradient(135deg,#102c59,#14608f 58%,#1e958f) !important; border: 1px solid rgba(255,255,255,.24); border-radius: 999px; box-shadow: 0 14px 32px rgba(20,50,99,.3); cursor: grab; }
.medhop-assistant__trigger * { color: inherit !important; }
.medhop-assistant__trigger-avatar { position: relative; display: grid; place-items: center; width: 46px; height: 46px; flex: 0 0 46px; color: #145b88 !important; background: linear-gradient(145deg,#fff,#dff7f2); border: 2px solid rgba(255,255,255,.82); border-radius: 50%; box-shadow: 0 4px 12px rgba(7,37,74,.22); font-size: 22px; overflow: visible; }
.medhop-assistant__trigger-avatar > i { color: #14608f !important; }
.medhop-assistant__trigger-avatar > img { display: block; width: 100%; height: 100%; object-fit: cover; object-position: center 18%; border-radius: 50%; pointer-events: none; }
.medhop-assistant__trigger-avatar small { position: absolute; top: -3px; right: -2px; display: grid; place-items: center; width: 17px; height: 17px; color: #fff !important; background: #f4a340; border: 2px solid #fff; border-radius: 50%; font-size: 7px; }
.medhop-assistant__trigger-copy { display: flex; flex-direction: column; align-items: flex-start; min-width: 92px; line-height: 1.05; }
.medhop-assistant__trigger-copy strong { color: #fff !important; font-size: 15px; font-weight: 800; letter-spacing: .01em; }
.medhop-assistant__trigger-copy small { margin-top: 4px; color: rgba(255,255,255,.78) !important; font-size: 9px; font-weight: 650; letter-spacing: .04em; text-transform: uppercase; }
.medhop-assistant--dragging .medhop-assistant__trigger { cursor: grabbing; transform: scale(1.02); box-shadow: 0 18px 38px rgba(20,50,99,.36); }
.medhop-assistant__panel { position: absolute; right: 0; bottom: 58px; display: flex; flex-direction: column; width: min(380px,calc(100vw - 28px)); max-height: min(620px,calc(100vh - 110px)); overflow: hidden; background: #fff; border: 1px solid #dfe7ef; border-radius: 18px; box-shadow: 0 24px 60px rgba(20,50,99,.22); }
.medhop-assistant__header { display: grid; grid-template-columns: 42px 1fr 32px; align-items: center; gap: 10px; padding: 14px 15px; color: #fff; background: linear-gradient(135deg,#102c59,#14608f 60%,#1e958f); }
.medhop-assistant__avatar { display: grid; place-items: center; width: 38px; height: 38px; overflow: hidden; color: #14608f; background: #fff; border: 1px solid rgba(255,255,255,.5); border-radius: 50%; font-size: 18px; }
.medhop-assistant__avatar img { display: block; width: 100%; height: 100%; object-fit: cover; object-position: center 18%; }
.medhop-assistant__header strong,.medhop-assistant__header span { display: block; }.medhop-assistant__header span { margin-top: 2px; opacity: .78; font-size: 10px; }.medhop-assistant__header button { width: 32px; height: 32px; color: #fff; background: transparent; border: 0; border-radius: 9px; }
.medhop-assistant__messages { min-height: 155px; max-height: 270px; overflow-y: auto; padding: 15px; background: #f7fafc; }
.medhop-assistant__messages article { display: flex; margin-bottom: 9px; }.medhop-assistant__messages article span { max-width: 88%; padding: 10px 12px; color: #334155; background: #fff; border: 1px solid #e2e8f0; border-radius: 13px 13px 13px 4px; font-size: 12px; line-height: 1.5; }.medhop-assistant__messages .is-user { justify-content: flex-end; }.medhop-assistant__messages .is-user span { color: #fff; background: #14608f; border-color: #14608f; border-radius: 13px 13px 4px 13px; }
.medhop-assistant__context { padding: 13px 15px 6px; }.medhop-assistant__context small { display: block; margin-bottom: 8px; color: #64748b; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; }.medhop-assistant__context div { display: flex; gap: 7px; overflow-x: auto; }.medhop-assistant__context button { flex: 0 0 auto; padding: 7px 10px; color: #145b88; background: #eef7f8; border: 1px solid #d7eaed; border-radius: 9px; font-size: 11px; font-weight: 600; }.medhop-assistant__context i { margin-right: 5px; }
.medhop-assistant__composer { display: grid; grid-template-columns: 1fr 38px; gap: 8px; padding: 10px 15px 8px; }.medhop-assistant__composer input { min-width: 0; height: 40px; padding: 0 12px; border: 1px solid #dbe4ed; border-radius: 11px; outline: 0; font-size: 12px; }.medhop-assistant__composer input:focus { border-color: #5aa8a3; box-shadow: 0 0 0 3px rgba(30,149,143,.1); }.medhop-assistant__composer button { color: #fff; background: #14608f; border: 0; border-radius: 11px; }.medhop-assistant__composer button:disabled { opacity: .45; }
.medhop-assistant__notice { margin: 0; padding: 0 15px 12px; color: #94a3b8; font-size: 9px; text-align: center; }
.assistant-panel-enter-active,.assistant-panel-leave-active { transition: opacity .18s ease,transform .18s ease; }.assistant-panel-enter,.assistant-panel-leave-to { opacity: 0; transform: translateY(10px) scale(.98); }
@media (max-width: 575.98px) { .medhop-assistant { right: 12px; bottom: 12px; }.medhop-assistant__trigger { min-height: 52px; padding: 4px 12px 4px 5px; }.medhop-assistant__trigger-avatar { width: 42px; height: 42px; flex-basis: 42px; font-size: 20px; }.medhop-assistant__panel { position: fixed; right: 8px; bottom: 72px; left: 8px; width: auto; max-height: calc(100vh - 86px); border-radius: 16px; }.medhop-assistant__messages { min-height: 130px; } }
</style>
